// 통계 탭 페이지 — 운영 인사이트 + 클럽 트렌드 + 내 기록을 합쳐 그린다.
//
// 1차 fetch(events·details·members·my-summaries)가 끝나면 위쪽 섹션을 즉시
// 렌더하고, 내 기록은 자체 fetch가 필요해 placeholder 자리를 먼저 잡고 그
// 자리를 나중에 교체. 사용자 체감상 위쪽이 빨리 떠오르도록.

import { api } from "../api";
import { isAdmin } from "../auth";
import { fetchMembers, state } from "../state";
import { h } from "../ui/dom";
import { buildStatsLoginCTA } from "../ui/loginCta";
import { buildAdminInsightsSection } from "./adminInsights";
import { buildClubTrendsSection } from "./clubTrends";
import { buildMyStatsSection } from "./myStats";

export async function renderStats(view: HTMLElement): Promise<void> {
  const memberId = state.session?.actor?.member_id;
  if (!memberId && !isAdmin()) {
    view.innerHTML = "";
    view.append(buildStatsLoginCTA());
    return;
  }
  view.innerHTML = "";
  view.append(h("div", { class: "loading" }, "불러오는 중…"));

  let events: any[] = [];
  try { events = await api("/api/v1/events"); } catch (_) { events = []; }
  // personal('내 모임') 이벤트는 클럽 통계/운영 인사이트에서 제외.
  // 본인 누적 기록(myStats)은 별도 /event-summaries 흐름이라 그쪽엔 그대로 포함됨.
  const clubEvents = events.filter((ev: any) => (ev.event_visibility || "club") !== "personal");
  const completedEvents = clubEvents
    .filter((ev: any) => ev.status === "completed")
    .sort((a: any, b: any) => (b.event_date || "").localeCompare(a.event_date || ""));
  const recentCompleted = completedEvents.slice(0, 5);

  // 1차 병렬: details / members / my-summaries
  const detailsPromise = Promise.all(
    recentCompleted.map((ev: any) => api(`/api/v1/events/${ev.event_id}`).catch(() => null)),
  ).then((arr) => arr.filter(Boolean));
  const membersPromise = fetchMembers().catch(() => []);
  const myDataPromise = memberId
    ? api(`/api/v1/members/${memberId}/event-summaries`).catch(() => null)
    : Promise.resolve(null);
  const [details, members, myData] = await Promise.all([
    detailsPromise, membersPromise, myDataPromise,
  ]);

  // 1차 데이터로 즉시 렌더 가능한 섹션을 먼저 그린다(운영 인사이트·클럽 트렌드).
  // 내 기록은 추가 fetch가 필요해 placeholder 자리만 잡고 비동기로 채움.
  view.innerHTML = "";

  if (isAdmin()) {
    view.append(buildAdminInsightsSection(details, members));
  }

  let myStatsSlot: HTMLElement | null = null;
  if (memberId) {
    myStatsSlot = h("div", {}, h("div", { class: "loading" }, "내 기록 불러오는 중…")) as HTMLElement;
    view.append(myStatsSlot);
  }

  view.append(buildClubTrendsSection(clubEvents, details, members));

  if (memberId && myStatsSlot) {
    try {
      const section = await buildMyStatsSection(myData, details, memberId, members, completedEvents);
      myStatsSlot.innerHTML = "";
      if (section) myStatsSlot.replaceWith(section);
      else myStatsSlot.remove();
    } catch (_) {
      myStatsSlot.remove();
    }
  }
}
